/**
 * RKL / VerifyDrawer — two-step verify flow.
 *
 * Per v3-architecture-spec §10: Test Mode entries are auto-verified and
 * never hit this drawer. Manual entries require the FSR to re-read the
 * target identifiers + attach an approval note.
 */

import { useState } from 'react';
import type { KillListEntryDto } from '@retail-ops/shared-types';
import { Button, Pill } from '@/components/primitives';

export interface VerifyDrawerProps {
  entry: KillListEntryDto;
  onClose: () => void;
  onConfirm: (note: string) => void;
}

export function VerifyDrawer({ entry, onClose, onConfirm }: VerifyDrawerProps): JSX.Element {
  const [confirmedTarget, setConfirmedTarget] = useState<boolean>(false);
  const [note, setNote] = useState<string>('');
  const canConfirm = confirmedTarget && note.trim().length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 420,
        background: 'var(--surface-card)',
        borderLeft: '1px solid var(--surface-border)',
        boxShadow: 'var(--sh-4)',
        padding: 'var(--sp-7)',
        overflowY: 'auto',
        zIndex: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)', marginBottom: 'var(--sp-6)' }}>
        <h3 style={{ margin: 0 }}>Verify kill entry</h3>
        <Pill variant="warn">Step 1 of 2</Pill>
      </div>

      <section
        style={{
          padding: 'var(--sp-5)',
          background: 'var(--ink-50)',
          borderRadius: 'var(--r-md)',
          marginBottom: 'var(--sp-5)',
          fontSize: 13,
        }}
      >
        <div>
          <strong>Entry:</strong> {entry.entryType}
        </div>
        <div>
          <strong>Target:</strong> <code>{entry.targetId}</code>
        </div>
        <div>
          <strong>Reason:</strong> {entry.reason}
        </div>
      </section>

      <label
        style={{
          display: 'flex',
          gap: 'var(--sp-3)',
          alignItems: 'flex-start',
          padding: 'var(--sp-4)',
          background: 'var(--warn-bg)',
          color: 'var(--warn-text)',
          borderRadius: 'var(--r-md)',
          fontSize: 13,
          marginBottom: 'var(--sp-5)',
        }}
      >
        <input
          type="checkbox"
          checked={confirmedTarget}
          onChange={(e) => setConfirmedTarget(e.target.checked)}
        />
        <span>
          I have re-read target <code>{entry.targetId}</code> and confirm this is the correct record
          to kill. I understand this will route to execute once verified.
        </span>
      </label>

      <label
        htmlFor="kl-verify-note"
        style={{
          display: 'block',
          fontSize: 11,
          textTransform: 'uppercase',
          color: 'var(--ink-500)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        Approval note (required)
      </label>
      <textarea
        id="kl-verify-note"
        rows={4}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        style={{
          width: '100%',
          padding: 'var(--sp-4)',
          border: '1px solid var(--ink-300)',
          borderRadius: 'var(--r-md)',
          fontFamily: 'var(--font-sans)',
          fontSize: 13,
        }}
      />

      <div style={{ display: 'flex', gap: 'var(--sp-3)', marginTop: 'var(--sp-6)' }}>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => onConfirm(note.trim())} disabled={!canConfirm}>
          Verify
        </Button>
      </div>
    </div>
  );
}
